import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Paper } from "@material-ui/core";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { FormPermissions } from "../../Permissions";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import EColourPanel from "../../interfaces/EColourPanel";
import ListLoading from "../util/Loading";
import IVenue from "../../yoello-lib/modules/API/interfaces/IVenue";
import { IReduxAppState } from "../../reducers";
import IVenueSettings from "../../interfaces/IVenueSettings";
import useTask from "../../hooks/useTask";
import { createDetailsFormGroups } from "../../formSchemas/details.form";
import { ILanguage } from "../../yoello-lib/modules/redux/actions/localization";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import { useVenueData } from "../../hooks/useVenueData";
import { detailsValidationResolver } from "../../formValidation/details.validation";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import ConfirmLocationChangeDialog from "../ordersSetting/Ui/confirmLocationChangeDialog";
// import Styles from "../../scss/details.module.scss"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    details: {
      "& form": {
        "& div": {
          justifyContent: "flex-start",
        },
      },
    },
    alert:{
      margin:"1.25rem 0",
      width:"80%"
    },
  })
);
export interface ILocation extends IVenue {
  location_name: string;
  address_postcode: string;
}

interface IDetails extends IVenue {
  location_name: string;
  address_number: string;
  address_building: string;
  address_postcode: { address_country: string; address_postcode: string };
  owner_name: string;
  owner_phone: string;
  owner_email: string;
  language_id: string;
  currency_id: string;
  address_country: string;
  time_zone_id: string;
  venue_type_id: { name: string; value: string };
  exo_number: string;
  vat_number: string;
}

export interface IVenueType {
  id: string;
  internal_name: string;
  deleted: boolean;
  created_on: string;
}

export type IVenueTypes = IVenueType[];

interface DetailsProps {
  venue: IVenue;
}

const Details = (props: DetailsProps) => {
  const { venue } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<IVenue>();
  const [isOpen, setIsOpen] = useState(false);

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.VENUE_MANAGEMENT);

  const { increaseDoneTasks } = useTask();

  const subscription = useSelector(
    (state: IReduxAppState) => state.subscription
  );

  const iCurrencies = useSelector(
    (state: IReduxAppState) => state.localization.currencies
  );
  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );
  const languages = useSelector(
    (state: IReduxAppState) => state.general.languages
  ) as ILanguage[];

  const VenueLocation = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueLocation
  );

  const timezones = useSelector((state: IReduxAppState) => state.timezones);
  // const isEposNow = useSelector((state: IReduxAppState) => state.isEposNow);

  const { callAPI: callSettings, apiData: settingsData } = useAPIV2<
    IVenueSettings
  >(APIExtensions.venueSettings, {
    canAPIGetCacheValue: true,
    selectorKeyParams: { venue_id: selectedVenueId },
    dispatcherParams: [selectedVenueId],
  });

  const { apiData: venueTypesData } = useAPIV2<IVenueTypes>(
    APIExtensions.venueTypes,
    {
      canAPIGetCacheValue: true,
      selectorKeyParams: { venue_id: selectedVenueId },
      dispatcherParams: [selectedVenueId],
    }
  );

  const { callAPI: callLocations, apiData: locationData } = useAPIV2<ILocation>(
    APIExtensions.locations,
    {
      canAPIGetCacheValue: true,
      query: venue?.location_id,
      selectorKeyParams: { venue_id: selectedVenueId },
      dispatcherParams: [selectedVenueId],
    }
  );

  const { callAPI: callVenues, apiData: venuesData } = useAPIV2<IVenue>(
    APIExtensions.venues,
    {
      query: selectedVenueId,
      canAPIGetCacheValue: true,
      selectorKeyParams: { venue_id: selectedVenueId },
      dispatcherParams: [selectedVenueId],
    }
  );

  const {callAPI: callPerciseLocation, apiData: locationsApiData} = useAPIV2<any>(APIExtensions.venues,{
    query: `${selectedVenueId}/locations`,
  })

  const { isSquare } = useVenueData(selectedVenueId);
  const { sendSnackbarMessage } = useSnackbarMessages()!;

  const transformFormData = (data: any) => {
    const venueType = venueTypesData?.find((item: any) => {
      return item.id === data.venue_type_id;
    });

    const venue_type_name = t(
      `forms.details.venueType_${venueType?.internal_name}`
    );

    return {
      ...data,
      venue_type_id: venueType ? { name: venue_type_name, value: venueType?.id } : undefined,
    };
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      /**if the api is loading still then we don't want to unset the loading because of the data setting */
      let loading = false;
      if (isLoading) {
        loading = true;
      }
      setIsLoading(true);
      let data: any = {};
      if (
        typeof settingsData === "object" &&
        typeof venuesData === "object" &&
        typeof locationData === "object"
      ) {
        data = {
          ...venuesData,
          ...locationData,
          ...settingsData,
          language_id : settingsData?.group_setting?.language_id??settingsData?.language_id
        };
        setIsLoading(loading);
      }

      setFormData(transformFormData(data) as IDetails);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venuesData, locationData, settingsData, venueTypesData]);

  const handleSubmit = async (data: any): Promise<void> => {
    setIsLoading(true);
    const {
      venue_name,
      location_name,
      address_number,
      address_building,
      address_postcode,
      venue_description,
      contact_phone,
      owner_name,
      owner_phone,
      owner_email,
      language_id,
      currency_id,
      address_country,
      time_zone_id,
      vat_number,
      exo_number
    } = data as IDetails;

    if (
      timezones?.findIndex((timezone) => timezone.id === time_zone_id) === -1 &&
      !isSquare
    ) {
      sendSnackbarMessage("Choose a valid timezone", "error");
      setIsLoading(false);
      return;
    }

    if (!(venue_name || "").trim()) {
      sendSnackbarMessage("Enter a valid venue name", "error");
      setIsLoading(false);
      return;
    }

    const venue_type_id = data.venue_type_id.value;

    const responseVenues = await callVenues(HTTPMethods.PUT, {
      query: venue?.id,
      body: {
        venue_name,
        venue_description,
        contact_phone,
        venue_type_id,
      },
    });

    const responseLocations = await callLocations(HTTPMethods.PUT, {
      query: formData?.location_id,
      body: {
        location_name,
        address_postcode: address_postcode,
        address_country,
        address_number,
        address_building,
      },
    });

    if ((location_name !== formData?.location_name || 
        address_postcode !== formData?.address_postcode ||
        address_country !== formData?.address_country ||
        address_number !== formData?.address_number ||
        address_building !== formData?.address_building) && VenueLocation?.address_country === "GB"
      ) {
      await callPerciseLocation(HTTPMethods.DELETE, {
        query: `${selectedVenueId}/locations/${locationsApiData?.[0]?.id}`
      })
      if (Boolean(locationsApiData?.[0]?.id)) {
        setIsOpen(true); 
      }
      
    }

    const responseSettings = await callSettings(HTTPMethods.PUT, {
      query: settingsData?.id,
      body: {
        owner_name,
        owner_phone,
        owner_email,
        language_id,
        currency_id,
        time_zone_id,
        vat_number,
        exo_number
      },
    });

    const response = {
      ...responseVenues.data,
      ...responseLocations.data,
      ...responseSettings.data,
    };

    //@ts-ignore
    setFormData(transformFormData(response));
    sendSnackbarMessage(t('forms.details.success'), 'success')
    setIsLoading(false);

    increaseDoneTasks();
  };

  const getVenueTypes = () => {
    return venueTypesData?.map((venueType: IVenueType) => {
      return {
        name: t(`forms.details.venueType_${venueType.internal_name}`),
        value: venueType.id,
      };
    });
  };
  return isLoading || !formData?.id ? (
    <ListLoading />
  ) : (
    <Paper style={{ padding: "3rem 4rem" }}>
      <ConfirmLocationChangeDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isLoading={isLoading}
        onConfirm={()=>{
          setIsOpen(false)
        }}
      />
      <div className={`dialog ${classes.details}`}>
        {settingsData?.group_setting?.language_id && <div className={classes.alert}>
        
        </div>}
        <DynamicForm
          onHandleSubmit={handleSubmit}
          disableAll={!hasPermission}
          disableAllInteraction={onFailedInteraction}
          useFormOptions={{
            resolver: (data) => detailsValidationResolver(data, isSquare),
            reValidateMode: "onChange",
            shouldFocusError: true,
          }}
          disabledFields={FormPermissions["merchantdetails"]}
          formData={formData}
          formGroups={createDetailsFormGroups(
            t,
            iCurrencies,
            languages,
            timezones || [],
            !formData.currency_id,
            //@ts-ignore
            !formData.address_country,
            getVenueTypes,
            settingsData?.group_setting?.language_id ? true : false
          )}
          subscriptionKey={subscription}
          formKeyName="submit"
          formButtons={{
            rightButton: {
              type: "submit",
              label: t("forms.tradingTimes.save"),
              color: EColourPanel.PRIMARY,
              disabled: !hasPermission
            },
          }}
        />
      </div>
    </Paper>
  );
};

export default Details;
